import {ChangeDetectorRef, Directive, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, Output} from '@angular/core';
import {AbstractControl, ControlValueAccessor, Validator} from '@angular/forms';
import {iterable} from 'app/core/helpers/object';
import {Subject, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

export interface ChangeEventData<T> {
    oldValue: T;
    newValue: T;
}

/* istanbul ignore next */
const ERROR_ARGUMENTS = new Map(
    iterable<ValidityState, (input?: HTMLInputElement) => any>({
        badInput: ({value}) => ({value}),
        patternMismatch: ({pattern, value}) => ({pattern, value}),
        rangeOverflow: ({max, value}) => ({max, value}),
        rangeUnderflow: ({min, value}) => ({min, value}),
        stepMismatch: ({step, value}) => ({step, value}),
        tooLong: ({maxLength, value}) => ({maxLength, value}),
        tooShort: ({minLength, value}) => ({minLength, value}),
        typeMismatch: ({type, value}) => ({type, value}),
        valueMissing: () => true,
    })
);

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class CustomFormComponent<V = string> implements ControlValueAccessor, Validator, OnDestroy {
    @Input() placeholder = '';
    @Input() id: string;
    @Input() name: string;
    @Input() disabled: boolean;
    @Input() required: boolean;
    @Input() maxLength: number;
    @Input() minLength: number;
    @Input() readonly: boolean;
    @Input() emitOnClose: boolean;
    @Input() invokeInitialChangeCallback = true;
    @Input() closeSubject: Subject<void> = new Subject<void>();
    @Output() valueChanges = new EventEmitter<ChangeEventData<V>>();
    @HostBinding('class.touched') touched: boolean;

    @HostBinding('class.invalid') get invalid(): boolean {
        return this.isInvalid();
    }

    internalValue: V = null;
    initialChange = new Set<V>();
    protected onChangeCallback: (newValue: V) => void;
    private onTouchCallback: (...args: Array<any>) => void;
    private customFormDestroy$ = new Subject();
    private emitValueSub = Subscription.EMPTY;

    constructor(
        public $el: ElementRef<HTMLElement>,
        public cd: ChangeDetectorRef
    ) {
        this.$el.nativeElement.removeAttribute('id');
    }

    writeValue(value: V) {
        this.internalValue = value;
        this.dismissInitialChange(value);
        this.cd.markForCheck();
    }

    registerOnChange(fn: (newValue: V) => void) {
        this.onChangeCallback = fn;
        if (this.invokeInitialChangeCallback) {
            setTimeout(() => {
                if (this.resolveInitialChange()) {
                    this.cd.markForCheck();
                }
            });
        }
    }

    registerOnTouched(fn: (...args: Array<any>) => void) {
        this.onTouchCallback = fn;
    }

    setDisabledState(state: boolean) {
        this.disabled = state;
        this.cd.markForCheck();
    }

    validate(control: AbstractControl): {[key: string]: any} {
        let input = this.getInput();
        if (input) {
            input.checkValidity();
        }
        if (input && !input.validity.valid) {
            return Object.keys(ValidityState.prototype).reduce((errors, property: keyof ValidityState) => {
                if (property !== 'valid' && input.validity[property]) {
                    errors[property] = ERROR_ARGUMENTS.get(property)(input);
                }
                return errors;
            }, {});
        }
        return null;
    }

    invokeChangeCallback(newValue: V, force = false) {
        if (typeof this.onChangeCallback === 'function') {
            let oldValue = this.internalValue;
            let emit = oldValue !== newValue || force;
            if (emit) {
                this.internalValue = newValue;
                this.onChangeCallback(newValue);

                if (this.emitOnClose && this.closeSubject) {
                    this.emitValueSub.unsubscribe();
                    this.emitValueSub = this.closeSubject.pipe(takeUntil(this.customFormDestroy$)).subscribe(() =>
                        this.valueChanges.emit({
                            oldValue,
                            newValue,
                        })
                    );
                } else {
                    this.valueChanges.emit({
                        oldValue,
                        newValue,
                    });
                }
            }
        } else {
            if (this.emitOnClose) {
                return;
            }

            this.scheduleInitialChange(newValue);
        }
    }

    invokeTouchCallback() {
        if (typeof this.onChangeCallback === 'function') {
            this.onTouchCallback();
        }
        this.touched = true;
    }

    isInvalid() {
        return false;
    }

    getInput(): HTMLInputElement {
        return null;
    }

    initInputValue<T extends keyof this = keyof this>(property: T, defaultValue: this[T]) {
        this[property] = this[property] === undefined ? defaultValue : this[property];
    }

    scheduleInitialChange(value: V) {
        this.initialChange.clear();
        this.initialChange.add(value);
    }

    dismissInitialChange(value: V) {
        if (this.initialChange.has(value)) {
            this.initialChange.clear();
        }
    }

    protected resolveInitialChange(): boolean {
        let markForCheck = !!this.initialChange.size;
        if (markForCheck) {
            this.invokeChangeCallback(this.initialChange.values().next().value);
            this.initialChange.clear();
        }
        return markForCheck;
    }

    ngOnDestroy() {
        this.customFormDestroy$.next();
    }
}
